// gpuUtils.js

/**
 * Parses an NVIDIA RTX card series string.
 * For example: "RTX 4070Ti-Super-16G" will be parsed into:
 * - baseNumber: 4070
 * - variantScore: a score based on the presence of "Ti" (+2) and "Super" (+1 each occurrence)
 * - memory: numeric memory size (if provided)
 */
export const parseRTXSeries = (series) => {
    // Remove "RTX" prefix and normalize memory units
    let s = series.replace(/^RTX\s*/i, '').trim().replace(/GB/gi, 'G');
    const parts = s.split('-');
    const basePart = parts[0]; // e.g. "4070Ti"
    const baseMatch = basePart.match(/(\d+)/);
    const baseNumber = baseMatch ? parseInt(baseMatch[1], 10) : 0;
    let variantScore = 0;
    const lowerBase = basePart.toLowerCase();
    if (lowerBase.includes('ti')) {
        variantScore += 2;
    }
    if (lowerBase.includes('super')) {
        variantScore += 1;
    }
    // Also check additional parts for "super"
    for (const part of parts.slice(1)) {
        if (part.toLowerCase().includes('super')) {
            variantScore += 1;
        }
    }
    let memory = 0;
    if (parts.length > 1) {
        const lastPart = parts[parts.length - 1];
        const memMatch = lastPart.match(/(\d+)/);
        if (memMatch) {
            memory = parseInt(memMatch[1], 10);
        }
    }
    return { baseNumber, variantScore, memory };
};

/**
 * Sorts two RTX series strings in descending order:
 * 1. Higher base number (e.g. 5080 > 4070)
 * 2. Higher variant score (e.g. "4070Ti-Super" beats plain "4070")
 * 3. Higher memory (e.g. 16G > 12G)
 */
export const sortRTX = (a, b) => {
    const parsedA = parseRTXSeries(a);
    const parsedB = parseRTXSeries(b);
    if (parsedA.baseNumber !== parsedB.baseNumber) {
        return parsedB.baseNumber - parsedA.baseNumber;
    }
    if (parsedA.variantScore !== parsedB.variantScore) {
        return parsedB.variantScore - parsedA.variantScore;
    }
    return parsedB.memory - parsedA.memory;
};

/**
 * Parses an AMD RX card series string.
 * For example: "RX 7900XTX" or "RX 7900 XTX" will be parsed into:
 * - baseNumber: 7900
 * - variantScore: 2 if "XTX" is present, 1 if "XT" is present, 0 otherwise.
 */
export const parseAMDSeries = (series) => {
    let s = series.replace(/^RX\s*/i, '').trim().toUpperCase();
    s = s.replace(/\s+/g, ''); // remove extra spaces
    const baseMatch = s.match(/^(\d+)/);
    const baseNumber = baseMatch ? parseInt(baseMatch[1], 10) : 0;
    let variantScore = 0;
    if (s.includes("XTX")) {
        variantScore = 2;
    } else if (s.includes("XT")) {
        variantScore = 1;
    }
    return { baseNumber, variantScore };
};

/**
 * Sorts two AMD RX series strings in descending order:
 * 1. Higher base number (e.g. 7900 > 7800)
 * 2. Higher variant score ("XTX" beats "XT", which beats none)
 */
export const sortAMD = (a, b) => {
    const parsedA = parseAMDSeries(a);
    const parsedB = parseAMDSeries(b);
    if (parsedA.baseNumber !== parsedB.baseNumber) {
        return parsedB.baseNumber - parsedA.baseNumber;
    }
    return parsedB.variantScore - parsedA.variantScore;
};

/**
 * General sort function for card series. If both start with "RTX" or both with "RX",
 * use the appropriate sort function. Otherwise, fall back to alphabetical order.
 */
export const sortCardSeries = (a, b) => {
    const upperA = a.toUpperCase();
    const upperB = b.toUpperCase();

    const isGTX_A = upperA.startsWith("GTX");
    const isGTX_B = upperB.startsWith("GTX");

    // If one is GTX and the other is not, force GTX to sort after non-GTX
    if (isGTX_A && !isGTX_B) return 1;
    if (isGTX_B && !isGTX_A) return -1;

    if (upperA.startsWith("RTX") && upperB.startsWith("RTX")) {
        return sortRTX(a, b);
    } else if (upperA.startsWith("RX") && upperB.startsWith("RX")) {
        return sortAMD(a, b);
    } else {
        return a.localeCompare(b);
    }
};


/**
 * For table sorting in GPUBrowserTable. For now, we re-use parseRTXSeries for "RTX" cards.
 * (For AMD cards, you might use parseAMDSeries instead.)
 */
export const parseCardSeries = (series) => {
    const upper = series.toUpperCase();
    if (upper.startsWith("RTX")) {
        const { baseNumber, variantScore } = parseRTXSeries(series);
        return { baseNumber, variantScore };
    } else if (upper.startsWith("RX")) {
        const { baseNumber, variantScore } = parseAMDSeries(series);
        return { baseNumber, variantScore };
    } else {
        // Fallback: extract any numbers
        const match = series.match(/(\d+)/);
        return { baseNumber: match ? parseInt(match[1], 10) : 0, variantScore: 0 };
    }
};

export const computePriceChange30Days = (priceHistory) => {
    if (!priceHistory || !priceHistory.Amazon) return 0;
    const records = priceHistory.Amazon.filter(
        record => record.price !== null && record.date
    );
    if (records.length === 0) return 0;
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const recentRecords = records.filter(
        record => new Date(record.date) >= thirtyDaysAgo
    );
    if (recentRecords.length < 2) return 0;
    recentRecords.sort((a, b) => new Date(a.date) - new Date(b.date));
    const firstPrice = recentRecords[0].price;
    const lastPrice = recentRecords[recentRecords.length - 1].price;
    if (firstPrice === 0) return 0;
    const percentChange = ((lastPrice - firstPrice) / firstPrice) * 100;
    return isNaN(percentChange) ? 0 : percentChange;
};
